import React from 'react';
import { skillScoreRawToBarPct, skillScoreRawToOutOf10 } from '../skillScoreUtils';
import AiEvalScoreLabel from './AiEvalScoreLabel';
import './FeedbackDashboard.css';

export interface CodingFeedbackReport {
  session: {
    session_id?: string;
    session_date?: string;
    problem_id?: string;
    language?: string;
    mode?: string;
    final_status?: string;
    final_score?: number;
    attempt_count?: number;
  };
  evaluation: {
    overall_score?: number;
    overall_summary?: string;
    dimensions?: Record<string, { score?: number; rationale?: string }>;
    feedback?: {
      summary?: string;
      strengths?: string[];
      improvements?: string[];
      critical_issues?: string[];
    };
    authenticity_assessment?: {
      confidence?: string;
      signals?: string[];
      concerns?: string[];
    };
  };
}

interface CodingFeedbackDashboardProps {
  codingFeedback: CodingFeedbackReport;
  candidateName?: string;
  onClose: () => void;
}

const formatLabel = (value?: string) => {
  if (!value) return 'Not available';
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getScoreLabel = (raw: number) => {
  const score = skillScoreRawToOutOf10(raw, 0);
  if (score >= 9) return 'Excellent';
  if (score >= 7) return 'Strong';
  if (score >= 5) return 'Fair';
  return 'Needs work';
};

const getOverallScoreColor = (score: number) => {
  if (score >= 85) return '#10B981';
  if (score >= 70) return '#F59E0B';
  return '#EF4444';
};

const CodingFeedbackDashboard: React.FC<CodingFeedbackDashboardProps> = ({
  codingFeedback,
  candidateName,
  onClose,
}) => {
  const session = codingFeedback.session || {};
  const evaluation = codingFeedback.evaluation || {};
  const feedback = evaluation.feedback || {};
  const authenticity = evaluation.authenticity_assessment || {};

  const formattedDate = session.session_date
    ? new Date(session.session_date).toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : 'Date unavailable';

  const overallScore = evaluation.overall_score ?? session.final_score ?? 0;
  const dimensionEntries = Object.entries(evaluation.dimensions || {});
  const attemptCount = session.attempt_count ?? 0;

  return (
    <div className="feedback-dashboard candidate-overlay">
      <div className="feedback-shell">
        <div className="feedback-header card-glass">
          <div className="header-left">
            <div className="header-info">
              <h1>{candidateName || 'Candidate'}</h1>
              <div className="interview-details">
                <span className="candidate-name">Coding session</span>
                <span className="interview-status">{formattedDate}</span>
              </div>
            </div>
          </div>
          <div className="header-actions">
            <button onClick={onClose} className="close-button">×</button>
          </div>
        </div>

        <div className="feedback-main-new">
          <div className="feedback-row feedback-row-top">
            <div className="feedback-cell avatar-cell">
              <div className="avatar-chip">CD</div>
              <div>
                <p className="eyebrow">Problem</p>
                <h3>{formatLabel(session.problem_id) || 'Coding challenge'}</h3>
                <p className="subtle-text">
                  {[formatLabel(session.language), formatLabel(session.mode), attemptCount ? `${attemptCount} attempt${attemptCount > 1 ? 's' : ''}` : '']
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
            </div>
            <div className="feedback-cell score-cell">
              <div className="overall-score-left">
                <AiEvalScoreLabel />
                <h2 style={{ color: getOverallScoreColor(overallScore) }}>
                  {Math.round(overallScore)}/100
                </h2>
              </div>
              <p className="score-subtitle">{formatLabel(session.final_status)}</p>
            </div>
          </div>

          <div className="feedback-row feedback-row-summary">
            <div className="feedback-sections feedback-sections-two">
              <div className="feedback-section">
                <h3>Overall summary</h3>
                <p className="feedback-summary-text">
                  {evaluation.overall_summary || 'No overall summary provided yet.'}
                </p>
              </div>
              <div className="feedback-section">
                <h3>Feedback summary</h3>
                <p className="feedback-summary-text">
                  {feedback.summary || 'No feedback summary provided yet.'}
                </p>
              </div>
            </div>
          </div>

          <div className="feedback-row feedback-row-dimensions">
            <div className="feedback-cell skills-cell">
              <div className="performance-header">
                <div>
                  <p className="eyebrow">Dimension scores</p>
                  <h3>Code evaluation</h3>
                </div>
              </div>
              <div className="skill-grid coding-dimension-grid">
                {dimensionEntries.length === 0 && (
                  <div className="skill-card coding-dimension-empty">
                    <p>No dimension scores were returned for this session.</p>
                  </div>
                )}
                {dimensionEntries.map(([key, value]) => {
                  const raw = value?.score ?? 0;
                  const out100 = Math.round(skillScoreRawToBarPct(raw, 0));
                  const barPct = skillScoreRawToBarPct(raw, 0);
                  const scoreLabel = getScoreLabel(raw);
                  return (
                    <div className="skill-card" key={key}>
                      <div className="skill-header">
                        <span>{formatLabel(key)}</span>
                        <strong>{out100}/100</strong>
                      </div>
                      <div className="skill-progress">
                        <div className="progress-bar" style={{ width: `${barPct}%` }} />
                      </div>
                      <p className="dimension-meta">{scoreLabel} performance in this area.</p>
                      <p>{value?.rationale || 'No rationale provided.'}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="feedback-row feedback-row-recommendations">
            <div className="feedback-sections">
              <div className="feedback-section">
                <h3>Strengths</h3>
                <ul>
                  {(feedback.strengths || []).length > 0
                    ? feedback.strengths?.map((item, idx) => <li key={idx}>{item}</li>)
                    : <li>No strengths were highlighted for this session.</li>}
                </ul>
              </div>
              <div className="feedback-section">
                <h3>Improvements</h3>
                <ul>
                  {(feedback.improvements || []).length > 0
                    ? feedback.improvements?.map((item, idx) => <li key={idx}>{item}</li>)
                    : <li>No improvement items were provided.</li>}
                </ul>
              </div>
              <div className="feedback-section">
                <h3>Critical issues</h3>
                <ul>
                  {(feedback.critical_issues || []).length > 0
                    ? feedback.critical_issues?.map((item, idx) => <li key={idx}>{item}</li>)
                    : <li>No critical issues were flagged.</li>}
                </ul>
              </div>
            </div>
          </div>

          <div className="feedback-row feedback-row-authenticity">
            <div className="feedback-sections feedback-sections-two">
              <div className="feedback-section">
                <h3>Authenticity assessment</h3>
                <p className="feedback-summary-text auth-confidence">
                  <span className="eyebrow">Confidence</span>
                  <strong>{formatLabel(authenticity.confidence)}</strong>
                </p>
                <p className="auth-heading">Signals</p>
                <ul>
                  {(authenticity.signals || []).length > 0
                    ? authenticity.signals?.map((item, idx) => <li key={idx}>{item}</li>)
                    : <li>No signals were listed.</li>}
                </ul>
              </div>
              <div className="feedback-section">
                <h3>Concerns</h3>
                <ul>
                  {(authenticity.concerns || []).length > 0
                    ? authenticity.concerns?.map((item, idx) => <li key={idx}>{item}</li>)
                    : <li>No concerns were listed.</li>}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CodingFeedbackDashboard;
